import React, { useEffect, useState } from 'react';

import { AnimatePresence } from 'framer-motion';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { ReactComponent as GoogleIcon } from '@pkg/assets/icons/v2/google.svg';
import { ReactComponent as MailIcon } from '@pkg/assets/icons/v2/mail.svg';
import { ReactComponent as AppleIcon } from '@pkg/assets/icons/v2/pay_apple.svg';
import { ReactComponent as DiscordIcon } from '@pkg/assets/icons/v2/social/discord.svg';
import { ReactComponent as TwitterIcon } from '@pkg/assets/icons/v2/social/twitter.svg';
import GemstoneUrl from '@pkg/assets/images/v2/emoji/gemstone.png';
import { RedirectSingleton, RedirectType } from '@pkg/classes';
import { FieldText, type FieldTextProps } from '@pkg/components';
import { useLogin, useProfile } from '@pkg/hooks';
import { TwoFactorAuthType } from '@pkg/types';

import { callProviderAuth } from '@app/client/resources/w3auth';
import { AuthProviders } from '@app/client/resources/w3auth/types';
import config from '@app/config';
import { YAT_PROFILE_PAGE } from '@app/routes/paths';
import { useAuthSidebar } from '@app/store/zustand';

import { Typography } from '../../DesignSystem';
import { DepButton, DepButtonDisplay, DepInputSize, DepPassword } from '../../deprecated';
import {
    ButtonIcon,
    ButtonItem,
    ButtonList,
    Container,
    ContentLegend,
    EmojiImage,
    EmphasizedTypography,
    FormGroup,
    FormMessage,
    FormMessageGroup,
    FormRow,
    FormRowReset,
    InternalLink,
    PrimaryCopySection,
    Section,
} from '../Web3Auth.style';
import { Web3AuthPanelRenderContext, Web3AuthSteps } from '../Web3Auth.types';
import { useWeb3AuthContext, useWeb3RenderContext } from '../context';

type ProviderButtonProps = {
    children: React.ReactNode;
    onClick: () => Promise<void>;
};
const ProviderButton = ({ children, onClick }: ProviderButtonProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const handleAuthProviderClick = () => {
        setIsLoading(true);
        onClick().finally(() => {
            setIsLoading(false);
        });
    };
    return (
        <DepButton
            loading={isLoading}
            variant="muted"
            display={DepButtonDisplay.block}
            onClick={handleAuthProviderClick}
        >
            {children}
        </DepButton>
    );
};

export function EntryPoint() {
    const { authProviders, twoFAWallEnabled } = config;
    const [translate] = useTranslation(['auth', 'common', 'fantasy_wishlist']);

    const { setAuthEmail, setCurrentStep } = useWeb3AuthContext();
    const { renderCtx } = useWeb3RenderContext();

    const location = useLocation();
    const navigate = useNavigate();
    const { loginWithPassword } = useLogin();
    const { redirectToProfile } = useProfile();
    const { postAuthRedirect } = useAuthSidebar();

    const [loading, setLoading] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [hasAuthError, setHasAuthError] = useState(false);

    const teamContext = renderCtx === Web3AuthPanelRenderContext.Teams;

    async function handleAuthProviderClick(provider: AuthProviders) {
        const link = await callProviderAuth(provider);

        if (link) {
            RedirectSingleton.setRedirect(RedirectType.Default, location.pathname);
            window.location.href = link;
        }
    }

    function handleEmailClick() {
        setCurrentStep(Web3AuthSteps.SignUpWithEmail);
    }

    function handleForgotClick() {
        setCurrentStep(Web3AuthSteps.ForgotPassword);
    }

    async function handleEmailLogin() {
        setLoading(true);
        setAuthEmail(email);

        try {
            const token = await loginWithPassword({ email, password });
            if (!token) throw new Error('Invalid email or password.');

            const twoFAShouldPrompt = Boolean(token?.requires_2fa && token?.requires_2fa !== TwoFactorAuthType.Invalid);
            if (twoFAWallEnabled && twoFAShouldPrompt) {
                setCurrentStep(Web3AuthSteps.TwoFactorAuth);
            } else {
                if (postAuthRedirect) {
                    if (postAuthRedirect === YAT_PROFILE_PAGE) {
                        await redirectToProfile({ hardRedirect: true });
                    } else {
                        navigate(postAuthRedirect);
                    }
                } else {
                    redirectToProfile();
                }
            }
        } catch (error) {
            setHasAuthError(true);
        }

        setLoading(false);
    }

    const handleEmailChange: FieldTextProps['onChange'] = (incomingValue) => {
        setEmail(`${incomingValue ?? ''}`);
    };

    const handlePasswordChange: FieldTextProps['onChange'] = (incomingValue) => {
        setPassword(`${incomingValue ?? ''}`);
    };

    useEffect(() => {
        if (hasAuthError) setHasAuthError(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [email, password]);

    const errorMarkup = hasAuthError ? (
        <FormMessageGroup key="FormMessageGroup">
            <FormMessage $type="error">Invalid email or password.</FormMessage>
        </FormMessageGroup>
    ) : null;

    return (
        <Container>
            <Section className="hide-on-tablet">
                <EmojiImage src={GemstoneUrl} />
            </Section>

            <PrimaryCopySection>
                <Typography type="headline" size={40}>
                    {teamContext
                        ? translate('w3a_panel_join_team_header', { ns: 'fantasy_wishlist' })
                        : translate('entry_point_heading')}
                </Typography>

                <Typography type="paragraph" size={18} faded={true}>
                    {translate('entry_point_subheading')}
                </Typography>
            </PrimaryCopySection>

            <Section>
                <ButtonList>
                    {authProviders.google && (
                        <ButtonItem>
                            <ProviderButton onClick={() => handleAuthProviderClick(AuthProviders.Google)}>
                                <ButtonIcon>
                                    <GoogleIcon />
                                </ButtonIcon>

                                <span>{translate('common:google')}</span>
                            </ProviderButton>
                        </ButtonItem>
                    )}

                    {authProviders.twitter && (
                        <ButtonItem>
                            <ProviderButton onClick={() => handleAuthProviderClick(AuthProviders.Twitter)}>
                                <ButtonIcon>
                                    <TwitterIcon style={{ fill: 'hsla(204, 88%, 53%, 1)' }} />
                                </ButtonIcon>

                                {translate('common:twitter')}
                            </ProviderButton>
                        </ButtonItem>
                    )}

                    {authProviders.apple && (
                        <ButtonItem>
                            <ProviderButton onClick={() => handleAuthProviderClick(AuthProviders.Apple)}>
                                <ButtonIcon>
                                    <AppleIcon />
                                </ButtonIcon>

                                {translate('common:apple')}
                            </ProviderButton>
                        </ButtonItem>
                    )}

                    {authProviders.discord && (
                        <ButtonItem>
                            <ProviderButton onClick={() => handleAuthProviderClick(AuthProviders.Discord)}>
                                <ButtonIcon>
                                    <DiscordIcon style={{ fill: 'hsla(235, 86%, 65%, 1)' }} />
                                </ButtonIcon>

                                {translate('common:discord')}
                            </ProviderButton>
                        </ButtonItem>
                    )}

                    <ButtonItem>
                        <DepButton variant="muted" display={DepButtonDisplay.block} onClick={handleEmailClick}>
                            <ButtonIcon>
                                <MailIcon />
                            </ButtonIcon>

                            {translate('common:email')}
                        </DepButton>
                    </ButtonItem>
                </ButtonList>

                <ContentLegend>
                    <Typography type="paragraph" size={16} color="rgba(255,255,255,0.5)" weight="700">
                        <Trans ns="auth" i18nKey="or_log_in_with_email">
                            <EmphasizedTypography>0</EmphasizedTypography>
                        </Trans>
                    </Typography>
                </ContentLegend>

                <FormGroup onSubmit={handleEmailLogin}>
                    <FormRow>
                        <FieldText
                            // Avoiding `email` type to prevent "error while typing".
                            // type="email"
                            autoFocus
                            label="Email"
                            value={email}
                            onChange={handleEmailChange}
                        />
                    </FormRow>

                    <FormRow>
                        {/* TODO: Update this element to <FieldText/>. This has been reverted
                        to DepPassword for now as we still need the ShowPassword functionality. */}
                        <DepPassword
                            type="password"
                            value={password}
                            label={translate('common:password')}
                            sizeVariant={DepInputSize.Compact}
                            autoFocus={false}
                            onChange={({ target }) => handlePasswordChange(target.value)}
                        />

                        <DepButton
                            type="submit"
                            variant="glow"
                            disabled={!(email && password)}
                            loading={loading}
                            onClick={handleEmailLogin}
                        >
                            {translate('email_login_submit')}
                        </DepButton>
                    </FormRow>

                    <FormRow>
                        <FormRowReset>
                            <AnimatePresence>{errorMarkup}</AnimatePresence>

                            <InternalLink href="#" onClick={handleForgotClick}>
                                {translate('forgot_password_action')}
                            </InternalLink>
                        </FormRowReset>
                    </FormRow>
                </FormGroup>
            </Section>
        </Container>
    );
}
